// src/pages/Connector/PrayerTiming/PrayerTimingConnector.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from "../../../../utils/axiosInstance";
import { hasPermission } from "../../../../utils/permissionUtils";
import {
    Box,
    Typography,
    Alert,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Tabs,
    Tab
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    School as TrainIcon,
    CalendarMonth as CalendarIcon,
    Mosque as MosqueIcon,
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import PrayerTimingConfigForm from './PrayerTimingConfigForm';
import PrayerTimingCalendar from './PrayerTimingCalendar';
import JumahManagement from './JumahManagement';

const CALCULATION_METHODS = [
    { value: 'ISNA', label: 'ISNA' },
    { value: 'MWL', label: 'Muslim World League' },
    { value: 'Egypt', label: 'Egyptian Authority' },
    { value: 'Makkah', label: 'Umm al-Qura, Makkah' },
    { value: 'Karachi', label: 'University of Karachi' },
    { value: 'Tehran', label: 'Tehran' },
    { value: 'Jafari', label: 'Jafari' }
];

export default function PrayerTimingConnector() {
    const navigate = useNavigate();
    const [configurations, setConfigurations] = useState([]);
    const [filteredConfigurations, setFilteredConfigurations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Pagination
    const [pagination, setPagination] = useState({
        page: 0,
        per_page: 10,
        total: 0
    });

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        method: '',
        status: 'all'
    });
    const [tabValue, setTabValue] = useState(0);

    // Dialog states
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
    const [jumahDialogOpen, setJumahDialogOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [configToDelete, setConfigToDelete] = useState(null);

    // Action states
    const [fetchingId, setFetchingId] = useState(null);
    const [trainingId, setTrainingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const { chatbotId: paramsChatbotId } = useParams();

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('prayer-timing', 'view');
                const modifyPerm = await hasPermission('prayer-timing', 'add|edit');
                const deletePerm = await hasPermission('prayer-timing', 'delete');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
                setCanDelete(deletePerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    // Fetch configurations
    const fetchConfigurations = useCallback(async () => {
        if (!paramsChatbotId) return;
        setIsLoading(true);
        setError('');

        try {
            const response = await axiosInstance.get('/api/prayer-timing/configurations', {
                params: { chatbot_id: paramsChatbotId }
            });
            const data = response.data.data || [];
            setConfigurations(data);
            setPagination(prev => ({ ...prev, total: data.length }));
        } catch (err) {
            console.error("Error fetching configurations:", err);
            setError(err.response?.data?.message || "Failed to fetch prayer timing configurations.");
        } finally {
            setIsLoading(false);
        }
    }, [paramsChatbotId]);

    useEffect(() => {
        document.title = 'Prayer Timing | KlonIT';
        fetchConfigurations();
    }, [fetchConfigurations]);

    // Apply filters and pagination
    useEffect(() => {
        let filtered = [...configurations];

        // Apply search filter
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(config =>
                config.title?.toLowerCase().includes(search) ||
                config.city?.toLowerCase().includes(search) ||
                config.country?.toLowerCase().includes(search)
            );
        }

        // Apply method filter
        if (filters.method) {
            filtered = filtered.filter(config => config.calculation_method === filters.method);
        }

        // Apply tab filter (status)
        switch (tabValue) {
            case 1: // Active
                filtered = filtered.filter(config => config.is_active);
                break;
            case 2: // Trained
                filtered = filtered.filter(config => config.is_trained);
                break;
            case 3: // Not Trained
                filtered = filtered.filter(config => !config.is_trained);
                break;
            default: // All
                break;
        }

        setPagination(prev => ({ ...prev, total: filtered.length, page: 0 }));
        setFilteredConfigurations(filtered);
    }, [configurations, searchTerm, filters, tabValue]);

    // Get paginated data
    const getPaginatedData = () => {
        const start = pagination.page * pagination.per_page;
        const end = start + pagination.per_page;
        return filteredConfigurations.slice(start, end);
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Handle filter change
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    // Clear filters
    const clearFilters = () => {
        setSearchTerm('');
        setFilters({ method: '', status: 'all' });
        setTabValue(0);
    };

    // Pagination handlers
    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleRowsPerPageChange = (event) => {
        setPagination(prev => ({ ...prev, per_page: parseInt(event.target.value, 10), page: 0 }));
    };

    // Handle add/edit config
    const handleOpenConfigDialog = (config = null) => {
        setEditingConfig(config);
        setConfigDialogOpen(true);
    };

    const handleCloseConfigDialog = () => {
        setConfigDialogOpen(false);
        setEditingConfig(null);
    };

    const handleSaveConfig = async (configData) => {
        try {
            if (editingConfig) {
                await axiosInstance.put(
                    `/api/prayer-timing/configurations/${editingConfig.id}`,
                    configData,
                    { params: { chatbot_id: paramsChatbotId } }
                );
                setSuccessMessage('Configuration updated successfully!');
            } else {
                await axiosInstance.post(
                    '/api/prayer-timing/configurations',
                    configData,
                    { params: { chatbot_id: paramsChatbotId } }
                );
                setSuccessMessage('Configuration created successfully!');
            }
            handleCloseConfigDialog();
            fetchConfigurations();
        } catch (err) {
            throw err;
        }
    };

    // Handle delete
    const handleOpenDeleteDialog = (config) => {
        setConfigToDelete(config);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setConfigToDelete(null);
    };

    const handleDeleteConfig = async () => {
        if (!configToDelete) return;

        setDeletingId(configToDelete.id);
        try {
            await axiosInstance.delete(
                `/api/prayer-timing/configurations/${configToDelete.id}`,
                { params: { chatbot_id: paramsChatbotId } }
            );
            setSuccessMessage('Configuration deleted successfully!');
            fetchConfigurations();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete configuration.");
        } finally {
            setDeletingId(null);
            handleCloseDeleteDialog();
        }
    };

    // Handle fetch prayer times
    const handleFetchPrayerTimes = async (configId, month = null, year = null) => {
        setFetchingId(configId);
        setError('');

        try {
            const payload = {};
            if (month) payload.month = month;
            if (year) payload.year = year;

            const response = await axiosInstance.post(
                `/api/prayer-timing/configurations/${configId}/fetch`,
                payload,
                { params: { chatbot_id: paramsChatbotId } }
            );
            setSuccessMessage(response.data.message || 'Prayer times fetched successfully!');
            fetchConfigurations();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch prayer times.");
        } finally {
            setFetchingId(null);
        }
    };

    // Handle train
    const handleTrain = async (configId) => {
        setTrainingId(configId);
        setError('');

        try {
            const response = await axiosInstance.post(
                `/api/prayer-timing/configurations/${configId}/train`,
                {},
                { params: { chatbot_id: paramsChatbotId } }
            );
            setSuccessMessage(response.data.message || 'Prayer times trained successfully!');
            fetchConfigurations();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to train prayer times.");
        } finally {
            setTrainingId(null);
        }
    };

    // Handle view calendar
    const handleOpenCalendar = (config) => {
        setSelectedConfig(config);
        setCalendarDialogOpen(true);
    };

    const handleCloseCalendar = () => {
        setCalendarDialogOpen(false);
        setSelectedConfig(null);
    };

    // Handle Juma'h management
    const handleOpenJumah = (config) => {
        setSelectedConfig(config);
        setJumahDialogOpen(true);
    };

    const handleCloseJumah = () => {
        setJumahDialogOpen(false);
        setSelectedConfig(null);
    };

    // Clear messages
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: '50vh', mt: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!canView) {
        return (
            <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
                <strong>Access Denied</strong><br />
                You don't have permission to view Prayer Timing.
            </Alert>
        );
    }

    const paginatedData = getPaginatedData();

    return (
        <Box sx={{ p: 3, mt: 10 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(`/chatbot/${paramsChatbotId}/connector`)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight={700}>
                            Prayer Timing Configurations
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {pagination.total} configuration{pagination.total !== 1 ? 's' : ''} found
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <IconButton onClick={fetchConfigurations} disabled={isLoading}>
                        <RefreshIcon />
                    </IconButton>
                    {canModify && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenConfigDialog()}
                        >
                            Add Configuration
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {successMessage && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            {/* Tabs for Quick Filters */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="All" />
                    <Tab label="Active" />
                    <Tab label="Trained" />
                    <Tab label="Not Trained" />
                </Tabs>
            </Paper>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by title, city, country..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Calculation Method</InputLabel>
                            <Select
                                value={filters.method}
                                label="Calculation Method"
                                onChange={(e) => handleFilterChange('method', e.target.value)}
                            >
                                <MenuItem value="">All Methods</MenuItem>
                                {CALCULATION_METHODS.map(method => (
                                    <MenuItem key={method.value} value={method.value}>
                                        {method.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button
                            variant="outlined"
                            startIcon={<ClearIcon />}
                            onClick={clearFilters}
                            fullWidth
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Configurations Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Title</strong></TableCell>
                                <TableCell><strong>Location</strong></TableCell>
                                <TableCell><strong>Method</strong></TableCell>
                                <TableCell><strong>Auto Train</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : paginatedData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography variant="h6" color="text.secondary" gutterBottom>
                                            No Configurations Found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {configurations.length === 0
                                                ? 'Add a configuration to start managing prayer times.'
                                                : 'Try adjusting your filters.'}
                                        </Typography>
                                        {canModify && configurations.length === 0 && (
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={() => handleOpenConfigDialog()}
                                            >
                                                Add Your First Configuration
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedData.map((config) => (
                                    <TableRow key={config.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                {config.title}
                                            </Typography>
                                            {config.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {config.description.substring(0, 50)}
                                                    {config.description.length > 50 ? '...' : ''}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {config.city && config.country
                                                    ? `${config.city}, ${config.country}`
                                                    : config.latitude && config.longitude
                                                    ? `${config.latitude}, ${config.longitude}`
                                                    : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {config.calculation_method || 'ISNA'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {config.auto_train_enabled ? (
                                                <Chip
                                                    label={`Day ${config.auto_train_day_of_month}`}
                                                    color="primary"
                                                    size="small"
                                                />
                                            ) : (
                                                <Chip label="Disabled" size="small" variant="outlined" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {config.is_trained ? (
                                                <Chip label="Trained" color="success" size="small" />
                                            ) : (
                                                <Chip label="Not Trained" color="warning" size="small" />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                <Tooltip title="View Calendar">
                                                    <IconButton
                                                        size="small"
                                                        color="info"
                                                        onClick={() => handleOpenCalendar(config)}
                                                    >
                                                        <CalendarIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Juma'h Management">
                                                    <IconButton
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => handleOpenJumah(config)}
                                                    >
                                                        <MosqueIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {canModify && (
                                                    <>
                                                        <Tooltip title="Fetch Prayer Times">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleFetchPrayerTimes(config.id)}
                                                                disabled={fetchingId === config.id}
                                                            >
                                                                {fetchingId === config.id ? (
                                                                    <CircularProgress size={20} />
                                                                ) : (
                                                                    <RefreshIcon />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Train to Chatbot">
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => handleTrain(config.id)}
                                                                disabled={trainingId === config.id}
                                                            >
                                                                {trainingId === config.id ? (
                                                                    <CircularProgress size={20} />
                                                                ) : (
                                                                    <TrainIcon />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenConfigDialog(config)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleOpenDeleteDialog(config)}
                                                            disabled={deletingId === config.id}
                                                        >
                                                            {deletingId === config.id ? (
                                                                <CircularProgress size={20} />
                                                            ) : (
                                                                <DeleteIcon />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={pagination.page}
                    onPageChange={handlePageChange}
                    rowsPerPage={pagination.per_page}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Paper>

            {/* Config Dialog */}
            <PrayerTimingConfigForm
                open={configDialogOpen}
                onClose={handleCloseConfigDialog}
                onSave={handleSaveConfig}
                config={editingConfig}
            />

            {/* Calendar Dialog */}
            {selectedConfig && calendarDialogOpen && (
                <PrayerTimingCalendar
                    open={calendarDialogOpen}
                    onClose={handleCloseCalendar}
                    config={selectedConfig}
                    chatbotId={paramsChatbotId}
                    canModify={canModify}
                    onFetch={handleFetchPrayerTimes}
                />
            )}

            {/* Juma'h Management Dialog */}
            {selectedConfig && jumahDialogOpen && (
                <JumahManagement
                    open={jumahDialogOpen}
                    onClose={handleCloseJumah}
                    config={selectedConfig}
                    chatbotId={paramsChatbotId}
                    canModify={canModify}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{configToDelete?.title}"?
                        This will also remove all associated prayer times and trained data.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button
                        onClick={handleDeleteConfig}
                        color="error"
                        variant="contained"
                        disabled={deletingId !== null}
                    >
                        {deletingId !== null ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
